import React, { useMemo, useState, useEffect } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState,
} from "@tanstack/react-table";
import { FilesTypes, UploadFilesTableData } from "@/types/file";
import styles from "./css/UploadFilesTable.module.css";
import { useNavigate, useLocation } from "react-router-dom";
import TruncateText from "../addons/TruncatedText";
import { useTranslation } from 'react-i18next';
import { langDirection } from "@/utils/languages";
import { formatDate } from "@/utils/date";
import { Button } from "../Buttons/Button";

interface FilesTableProps {
  className?: string;
  dataTestId: string;
  files: UploadFilesTableData[];
  onUploadFile: (type: FilesTypes, file: File) => void;
  showFull?: boolean;
  rows?: number;
}

export const UploadFilesTable: React.FC<FilesTableProps> = ({
  className,
  dataTestId,
  files,
  onUploadFile,
  showFull = false,
  rows = 4
}) => {

  const { t, i18n } = useTranslation('CreateProjectPage');
  const direction = langDirection(i18n.language)
  const navigate = useNavigate();
  const colors = ["green", "pink", "yellow", "blue"];
  const randomColor = () => colors[Math.floor(Math.random() * colors.length)];

  const location = useLocation();
  const decodedPathname = decodeURIComponent(location.pathname);

  // Use state instead of constant for maxLength
  const [maxLength, setMaxLength] = useState(() => 
    window.innerWidth >= 1536 ? 30 : 18
  );

  // Add resize listener
  useEffect(() => {
    const handleResize = () => {
      setMaxLength(window.innerWidth >= 1536 ? 30 : 18);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleUploadFile = (type: FilesTypes) => {
    // Create a hidden file input element
    const acceptTypes = files.find(file => file.type === type)?.accept_types.join(',') || '';
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = acceptTypes;
    fileInput.style.display = 'none';

    // Add it to the document
    document.body.appendChild(fileInput);

    // Handle file selection
    fileInput.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      
      if (file) {
        if (fileInput.accept.includes(file.type) || fileInput.accept.includes(file.name.split('.').pop() || '')) {
          onUploadFile(type, file);
        } else {
          // TODO: Add proper error handling/notification
          console.error('Please select a CSV file');
        }
      }

      // Clean up
      document.body.removeChild(fileInput);
    };

    // Trigger file selection dialog
    fileInput.click();
  };


  const columns = useMemo<ColumnDef<UploadFilesTableData>[]>(
    () => {
      const baseColumns: ColumnDef<UploadFilesTableData>[] = [
        {
            accessorKey: "type",
            header: () => <div data-testid="type-column-header" className={styles.headerText}>{t('Type')}</div>,
            cell: ({ row }) => {
                return (
                    <div data-testid={`type-cell-${row.original.type}`}>{t(row.original.type)}</div>
                );
            },
        },
        {
          accessorKey: "name",
          header: () => <div data-testid="name-column-header" className={styles.headerText}>{t('FileName')}</div>,
          cell: ({ row }) => {
            return (
                <span
                  data-testid={`${dataTestId}-${row.original.file_name}`}
                  className={`${styles.fileName}`}
                >
                  <TruncateText 
                    text={row.original.file_name} 
                    maxLength={maxLength}
                  />
                </span>
            );
          },
          sortingFn: (rowA, rowB) => {
            return rowA.original.file_name.localeCompare(rowB.original.file_name);
          },
        },
        {
          accessorKey: "created_at",
          header: () => <div data-testid="created-at-column-header" className={styles.headerText}>{t('CreatedAt')}</div>,
          cell: ({ row }) => (
            <div data-testid={`created-at-cell-${row.original.created_at}`} className="mr-[10px]">
              {formatDate(row.original.created_at)}
            </div>
          ),
          sortingFn: (rowA, rowB) => {
            const parseDateTime = (dateTimeStr: string) => {
              const [date, time] = dateTimeStr.split(' ');
              const [day, month, year] = date.split('/').map(Number);
              const [hours, minutes, seconds] = time.split(':').map(Number);
              return new Date(2000 + year, month - 1, day, hours, minutes, seconds);
            };

            const dateA = parseDateTime(rowA.original.created_at);
            const dateB = parseDateTime(rowB.original.created_at);
            return dateA.getTime() - dateB.getTime();
          },
        },
        {
          accessorKey: "actions",
          header: () => <div data-testid="actions-column-header" className={styles.headerText}>{t("Actions")}</div>,
          cell: ({ row }) => (
            <div data-testid={`actions-cell-${row.original.file_name}`} className="flex flex-row gap-2 2xl:gap-4">
              <Button
                icon="no"
                text={t("uploadFile")}
                id={`view-button-${row.original.file_name}`}
                onClick={() => handleUploadFile(row.original.type)}
                className={styles.uploadFileButton}
                size="fit"
                color="blue"
                
              />
            </div>
            
          )
        }
      ];

      return baseColumns;
    },
    [navigate, randomColor, decodedPathname, maxLength]
  );

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: files,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (files.length === 0) {
    return (
      <div className={`${showFull ? styles.tableContainerFull : styles.tableContainer} ${className}`}
      style={{ 
        height: `${(rows * 48) + 48}px`, // 48px per row + 48px for header
        overflow: 'auto',
        width: '100%'
      }}>
        <table className={styles.emptyTable}>
          <thead className={styles.tableHeadersRow}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={`${styles.headerText} ${styles.tableHeader} ${styles[direction]} ${
                      header.id === 'checkbox' ? styles.checkboxHeader : ''
                    }`}
                  >
                    <div>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            <tr>
              {columns.map((column, index) => (
                <td key={index} className={styles.emptyCell}></td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div 
      className={`${showFull ? styles.tableContainerFull : styles.tableContainer} ${className} ${styles[direction]}`}
      style={{ 
        height: `${(rows * 48) + 48}px`, // 48px per row + 48px for header
        overflow: 'auto',
        width: '100%'
      }}
    >
      <table className={`${styles.table} ${styles[direction]}`}>
        <thead className={styles.tableHeadersRow}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  data-column={header.column.id}
                  className={`${styles.headerText} ${styles.tableHeader} ${styles[direction]} ${
                    header.id === 'checkbox' ? styles.checkboxHeader : ''
                  }`}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className={styles.bodyRow}>
          {table.getRowModel().rows.map((row) => (
            <tr 
              key={row.id} 
              className={`${styles.tableRow}`}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  data-column={cell.column.id}
                  className={`${styles.rowText} ${styles.tableCell} ${styles[direction]} 
                    ${cell.column.id === "name" ? styles.fileName : ""}`}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UploadFilesTable;
